import React, { useState, useEffect, useCallback } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Loader2, ShieldCheck, ShieldX, FileText, Clock, CheckCircle, XCircle,
  ChevronDown, ChevronUp, Search, User
} from 'lucide-react';
import api from '../../utils/api';

const STATUS_STYLES = {
  verified: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-100 text-red-700',
  unverified: 'bg-slate-100 text-slate-500',
};

function adminHeaders() {
  return {};
}

function DoctorRow({ doctor, onSelect, isSelected }) {
  const ef = doctor.eligibility_flags || {};
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'ring-2 ring-teal-500 bg-teal-50' : 'hover:bg-slate-50'}`}
      onClick={() => onSelect(doctor.doctor_id)}
      data-testid={`doctor-row-${doctor.doctor_id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{doctor.full_name || 'Unnamed'}</p>
        <p className="text-xs text-slate-400">{doctor.email}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {ef.is_searchable && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Searchable</Badge>}
        <Badge className={`${STATUS_STYLES[doctor.verification_status] || STATUS_STYLES.unverified} text-[10px]`}>
          {doctor.verification_status || 'unverified'}
        </Badge>
      </div>
    </div>
  );
}

function DocumentCard({ doc, doctorId, onAction }) {
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();

  const handleVerify = async () => {
    setActionLoading('verify');
    try {
      await api.post(`/admin/doctors/${doctorId}/verify-document?document_id=${doc.document_id}`, { notes: '' }, adminHeaders());
      onAction();
    } catch {} finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    setActionLoading('reject');
    try {
      await api.post(`/admin/doctors/${doctorId}/reject-document?document_id=${doc.document_id}`, { reason: rejectReason }, adminHeaders());
      setShowReject(false);
      onAction();
    } catch {} finally { setActionLoading(null); }
  };

  return (
    <div className={`border rounded-lg p-3 ${isExpired ? 'border-red-200 bg-red-50' : ''}`} data-testid={`doc-card-${doc.document_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-400" />
          <div>
            <p className="text-sm font-medium">{doc.document_name || doc.file_name}</p>
            <p className="text-xs text-slate-400">
              {doc.document_type}
              {doc.expiry_date && <> | Expires: {doc.expiry_date}</>}
              {isExpired && <span className="text-red-500 font-medium ml-1">EXPIRED</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${STATUS_STYLES[doc.verification_status] || STATUS_STYLES.pending} text-[10px]`}>
            {doc.verification_status || 'pending'}
          </Badge>
          {doc.verification_status !== 'verified' && (
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={handleVerify} disabled={!!actionLoading} data-testid={`verify-btn-${doc.document_id}`}>
              {actionLoading === 'verify' ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />} Verify
            </Button>
          )}
          {doc.verification_status !== 'rejected' && (
            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => setShowReject(!showReject)} disabled={!!actionLoading} data-testid={`reject-toggle-${doc.document_id}`}>
              <XCircle className="h-3 w-3 mr-1" /> Reject
            </Button>
          )}
        </div>
      </div>
      {doc.rejection_reason && (
        <p className="text-xs text-red-500 mt-1">Reason: {doc.rejection_reason}</p>
      )}
      {showReject && (
        <div className="mt-2 flex gap-2">
          <Input placeholder="Rejection reason" value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="text-xs h-8" data-testid={`reject-reason-${doc.document_id}`} />
          <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={handleReject} disabled={!!actionLoading} data-testid={`reject-confirm-${doc.document_id}`}>
            {actionLoading === 'reject' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm Reject'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AdminDoctorVerification() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchDoctors = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}&limit=100` : '?limit=100';
      const { data } = await api.get(`/admin/doctors${params}`, adminHeaders());
      if (data.success) setDoctors(data.doctors || []);
    } catch {} finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  const fetchDetail = useCallback(async (doctorId) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/doctors/${doctorId}`, adminHeaders());
      if (data.success) setDetail(data);
    } catch {} finally { setDetailLoading(false); }
  }, []);

  const handleSelect = (doctorId) => {
    setSelectedId(doctorId);
    fetchDetail(doctorId);
  };

  const handleDocAction = () => {
    if (selectedId) fetchDetail(selectedId);
    fetchDoctors();
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;
  }

  const dr = detail?.doctor;
  const ef = dr?.eligibility_flags || {};

  return (
    <div data-testid="admin-doctor-verification-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal-600" /> Doctor Verification</h2>
        <div className="flex gap-2">
          {['', 'unverified', 'pending', 'verified', 'rejected'].map(s => (
            <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setStatusFilter(s)} data-testid={`filter-${s || 'all'}`}>
              {s || 'All'}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Doctor list */}
        <div className="lg:col-span-2 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="doctor-list">
          {doctors.map(d => (
            <DoctorRow key={d.doctor_id} doctor={d} onSelect={handleSelect} isSelected={selectedId === d.doctor_id} />
          ))}
          {doctors.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No doctors found</p>}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-3">
          {detailLoading ? (
            <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
          ) : detail && dr ? (
            <Card data-testid="doctor-detail-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{dr.full_name}</CardTitle>
                  <Badge className={`${STATUS_STYLES[dr.verification_status] || STATUS_STYLES.unverified}`}>
                    {dr.verification_status}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400">{dr.email} | ID: {dr.doctor_id}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Eligibility Flags */}
                <div className="flex gap-2 flex-wrap" data-testid="eligibility-flags">
                  <Badge variant="outline" className={`text-xs ${ef.is_searchable ? 'border-emerald-300 text-emerald-600' : 'border-slate-200 text-slate-400'}`}>
                    {ef.is_searchable ? 'Searchable' : 'Not Searchable'}
                  </Badge>
                  <Badge variant="outline" className={`text-xs ${ef.is_assignable ? 'border-emerald-300 text-emerald-600' : 'border-slate-200 text-slate-400'}`}>
                    {ef.is_assignable ? 'Assignable' : 'Not Assignable'}
                  </Badge>
                  <Badge variant="outline" className={`text-xs ${ef.is_public_visible ? 'border-emerald-300 text-emerald-600' : 'border-slate-200 text-slate-400'}`}>
                    {ef.is_public_visible ? 'Public Visible' : 'Not Public'}
                  </Badge>
                </div>

                {/* Verification Summary */}
                {dr.verification_summary && (
                  <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1" data-testid="verification-summary">
                    <p>Verified docs: {dr.verification_summary.verified_documents_count || 0} / {dr.verification_summary.required_documents_count || 2}</p>
                    <p>Mandatory verified: {dr.verification_summary.verified_mandatory || 0} / {dr.verification_summary.total_mandatory || 2}</p>
                    {dr.verification_summary.last_verified_at && <p>Last verified: {formatDateTimeLocale(dr.verification_summary.last_verified_at)}</p>}
                  </div>
                )}

                {/* Documents */}
                <div>
                  <h3 className="text-sm font-medium mb-2 flex items-center gap-1"><FileText className="h-4 w-4" /> Documents ({(detail.documents || []).length})</h3>
                  <div className="space-y-2" data-testid="documents-section">
                    {(detail.documents || []).map(doc => (
                      <DocumentCard key={doc.document_id} doc={doc} doctorId={dr.doctor_id} onAction={handleDocAction} />
                    ))}
                    {(detail.documents || []).length === 0 && <p className="text-xs text-slate-400">No documents uploaded</p>}
                  </div>
                </div>

                {/* Affiliations */}
                {(detail.affiliations || []).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Affiliations ({detail.affiliations.length})</h3>
                    <div className="space-y-1">
                      {detail.affiliations.slice(0, 5).map(aff => (
                        <div key={aff.affiliation_id} className="flex items-center justify-between text-xs bg-slate-50 rounded p-2">
                          <span>{aff.provider_name || aff.provider_id} ({aff.role})</span>
                          <Badge className={`${STATUS_STYLES[aff.status] || ''} text-[10px]`}>{aff.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-slate-400">
              <User className="h-8 w-8 mb-2" />
              <p className="text-sm">Select a doctor to review</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
